import type { PixelBudget } from "./pixel-budget";
/** Disposable display copies. Source images used by sampling and export remain untouched. */
export function imageLevel(
  width: number,
  height: number,
  pixelWidth: number,
  pixelHeight: number,
) {
  const ratio = Math.min(
    1,
    Math.max(pixelWidth / width, pixelHeight / height) * 1.25,
  );
  const edge = Math.min(
    Math.max(width, height),
    Math.max(64, 2 ** Math.ceil(Math.log2(Math.max(width, height) * ratio))),
  );
  const scale = edge / Math.max(width, height);
  return {
    width: Math.max(1, Math.ceil(width * scale)),
    height: Math.max(1, Math.ceil(height * scale)),
  };
}
interface LevelBuilder {
  available(): boolean;
  resize(
    image: HTMLImageElement,
    width: number,
    height: number,
  ): Promise<ImageBitmap>;
}
export class ImageLevels {
  private entries = new Map<
    HTMLCanvasElement | ImageBitmap,
    HTMLImageElement
  >();
  private pending = new Map<HTMLImageElement, Set<string>>();
  private epoch = 0;
  private failed = new WeakMap<HTMLImageElement, Set<string>>();
  private bytes = 0;
  constructor(
    private budget = 32 * 1024 * 1024,
    private builder?: LevelBuilder,
    private changed = () => {},
    private shared?: PixelBudget,
  ) {}
  get(image: HTMLImageElement, pixelWidth: number, pixelHeight: number) {
    const size = imageLevel(
      image.naturalWidth,
      image.naturalHeight,
      pixelWidth,
      pixelHeight,
    );
    const previous = [...this.entries].find(
      ([canvas, source]) =>
        source === image &&
        canvas.width >= size.width &&
        canvas.height >= size.height,
    )?.[0];
    if (
      size.width >= image.naturalWidth &&
      size.height >= image.naturalHeight
    ) {
      return image;
    }
    if (previous) {
      this.entries.delete(previous);
      this.entries.set(previous, image);
      this.shared?.touch(previous);
      return previous;
    }
    const cost = size.width * size.height * 4;
    if (cost > this.budget) return image;
    if (this.builder?.available()) {
      const key = size.width + "x" + size.height,
        epoch = this.epoch;
      if (this.failed.get(image)?.has(key)) return image;
      let pending = this.pending.get(image);
      if (!pending) {
        pending = new Set();
        this.pending.set(image, pending);
      }
      if (!pending.has(key)) {
        pending.add(key);
        void this.builder
          .resize(image, size.width, size.height)
          .then((bitmap) => {
            if (epoch !== this.epoch) {
              bitmap.close();
              return;
            }
            while (this.bytes + cost > this.budget && this.entries.size)
              this.remove(this.entries.keys().next().value!);
            this.seed(image, bitmap);
          })
          .catch(() => {
            if (epoch !== this.epoch) return;
            let failed = this.failed.get(image);
            if (!failed) {
              failed = new Set();
              this.failed.set(image, failed);
            }
            failed.add(key);
          })
          .finally(() => {
            pending.delete(key);
            if (!pending.size && this.pending.get(image) === pending)
              this.pending.delete(image);
            this.changed();
          });
      }
      const candidates = [...this.entries]
        .filter(([, source]) => source === image)
        .map(([copy]) => copy)
        .sort((a, b) => a.width - b.width);
      return (
        candidates.find((copy) => copy.width >= size.width) ??
        candidates.at(-1) ??
        image
      );
    }
    while (this.bytes + cost > this.budget && this.entries.size)
      this.remove(this.entries.keys().next().value!);
    const canvas = document.createElement("canvas");
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext("2d")!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(image, 0, 0, size.width, size.height);
    this.seed(image, canvas);
    return canvas;
  }
  seed(image: HTMLImageElement, canvas: HTMLCanvasElement | ImageBitmap) {
    const cost = canvas.width * canvas.height * 4;
    while (this.bytes + cost > this.budget && this.entries.size)
      this.remove(this.entries.keys().next().value!);
    this.entries.set(canvas, image);
    this.bytes += cost;
    this.shared?.add(canvas, cost, "imageLevels", () => this.remove(canvas));
  }
  private remove(canvas: HTMLCanvasElement | ImageBitmap) {
    if (!this.entries.has(canvas)) return;
    this.shared?.remove(canvas);
    this.bytes -= canvas.width * canvas.height * 4;
    if ("close" in canvas) canvas.close();
    else canvas.width = canvas.height = 0;
    this.entries.delete(canvas);
  }
  retain(images: Set<HTMLImageElement>) {
    this.epoch++;
    this.pending.clear();
    for (const [canvas, image] of this.entries)
      if (!images.has(image)) this.remove(canvas);
  }
  stats() {
    return {
      count: this.entries.size,
      rgbaBytes: this.bytes,
      budget: this.budget,
    };
  }
}
